import { useMutation } from '@apollo/client'
import { APIStatus } from '@flowwai-frontend/shared/api-common'
import {
   GoogleAuthClientType,
   NETWORK_ONLY_FETCH_POLICY
} from '@flowwai-frontend/bps-officer-web/graphql'

import CustomError from '../../models/CustomError'
import { convertToErrorType, getAPIStatus } from '../../utils/APIUtils'

import {
   AuthorizeGoogleUserMutation as Mutation,
   AuthorizeGoogleUserDocument as MutationDocument,
   AuthorizeGoogleUserMutationVariables as MutationVariables
} from './mutation.generatedTypes'
import useResponseHandler from './responseHandler'

export interface FailureArgsType {
   data?: CustomError<Mutation['authorizeGoogleUser']>
   error?: Error | null
}

interface APIArgs {
   authorizationCode: string
   email?: string

   onFailure?: (args: FailureArgsType) => void
   onSuccess: () => void
}

interface ReturnType {
   apiError: Error | null
   apiStatus: APIStatus

   triggerAPI: (args: APIArgs) => void
}

const useAuthorizeGoogleUser = (): ReturnType => {
   const [authorizeGoogleUser, { error, loading }] = useMutation<
      Mutation,
      MutationVariables
   >(MutationDocument)

   const { handleAPIResponse } = useResponseHandler()

   const triggerAPI = async (args: APIArgs): Promise<void> => {
      const requestObj: MutationVariables = {
         params: {
            authorizationCode: args.authorizationCode,
            email: args.email,
            authClientType: GoogleAuthClientType.Web
         }
      }

      const response = await authorizeGoogleUser({
         fetchPolicy: NETWORK_ONLY_FETCH_POLICY,
         variables: requestObj,

         onError(error) {
            args.onFailure?.({ error })
         }
      })
      if (!response.data) return

      handleAPIResponse({
         data: response.data,

         onHandleError: (
            errorMessage: string,
            data: Mutation['authorizeGoogleUser']
         ) => args.onFailure?.({ data: new CustomError(errorMessage, data) }),
         onSuccess: args.onSuccess
      })
   }

   const apiError = convertToErrorType(error)
   const apiStatus = getAPIStatus({
      isError: Boolean(error),
      isLoading: loading
   })

   return {
      apiError,
      apiStatus,

      triggerAPI
   }
}

export default useAuthorizeGoogleUser
